import {ExternalCompletionDetails} from "../../components/ExternalCompletion";
import {FailureEvidenceList} from "../../components/MerchantCancellation";
import {CloseSellDispute} from "./CloseSellDispute";
import {AdminDepositCasesPage} from "./AdminDepositCasesPage";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    ApiError,
    downloadReceipt,
    listTraders,
    openDisputePayment,
    resolveDispute,
    resolveOrderDispute,
    uploadDisputeReceipt,
} from "../../api";
import type {AppealItem, DisputeReceipt, ResolveDisputeOutcome, ResolveDisputeRequest} from "../../api";
import {useAuth} from "../../auth";
import {ErrorBanner, Modal} from "../../components";
import {PortalReceiptList} from "../../components/PortalReceiptList";
import {PayinParts} from "../../components/PayinParts";
import {confirmPayinPart} from "../../api/merchantPortalPayin";
import {
    RECEIPT_FILE_ACCEPT,
    RECEIPT_FILE_HINT,
    isSupportedReceiptFile,
} from "../../components/payment-details";
import {formatAmount, formatDate, resolveEffectiveRate} from "../../utils";
import {
    activeTraderOptions,
    disputeFailureError,
    disputeSuccessAmountError,
    disputeTraderChoiceError,
    exchangeClientSignalsSummary,
    exchangeOutcomeWarning,
    resolutionCardClass,
    type TraderOption,
} from "./disputeResolution";

const MAX_RECEIPT_BYTES = 15 * 1024 * 1024;

export function isSellDisputeActionAllowed(workflowKind: string | undefined): boolean {
    return workflowKind !== "exchange_sell";
}

interface DisputeDetailsProps {
    item: AppealItem;
    initialCaseId?: string | null;
    onBack: () => void;
    /**
     * Ключ — ЗАЯВКА, а не платёж. Разобранная заявка без держателя платежа не
     * имеет вовсе, а список споров и так ключуется заявкой: платёж под админом
     * меняется, заявка одна.
     */
    onResolved: (orderId: string, merchantStatus: string) => void;
    onConflict: (err: unknown) => void;
    onReviewChanged?: () => void;
}

interface ReceiptDraft {
    /** Ключ списка. У двух файлов может совпасть всё, вплоть до имени. */
    key: string;
    file: File;
    fileName: string;
    fileSize: number;
    objectUrl: string;
    /** id returned by the successful upload; retries present it again */
    receiptId?: string;
}

/**
 * Технический потолок на одно закрытие, тот же, что и на бэкенде
 * (MaxPresentedReceipts). Продуктового лимита нет: сколько переводов было,
 * столько чеков и прикладывают.
 */
const MAX_RECEIPTS = 20;

let receiptKeySeq = 0;

interface PendingAction {
    outcome: ResolveDisputeOutcome;
    merchantStatus: "OK" | "PROCESS" | "CANCEL";
    title: string;
    message: string;
    confirmLabel: string;
    buttonClass: string;
    body: ResolveDisputeRequest;
    // Новые файлы загружаются перед разбором; ранее загруженные выбираются
    // отдельно в existingReceiptIds.
    receipts: ReceiptDraft[];
    /** Existing payment receipts explicitly selected for this resolution. */
    existingReceiptIds: string[];
    /**
     * Трейдер, за которым будут числиться деньги. Задан только у заявки без
     * держателя: там платёж ещё предстоит открыть. У заявки с живым платежом
     * трейдер уже есть, и закрытие не имеет права его переназначать — оно
     * закрывает ЕГО платёж.
     */
    traderId?: string;
}

export function defaultSelectedDisputeReceiptIds(receipts: DisputeReceipt[] | null | undefined): string[] {
    return receipts?.length === 1 ? [receipts[0].receiptId] : [];
}

export function hasSelectedDisputeReceipt(args: {
    stagedCount: number;
    uploadedCount: number;
    selectedCount: number;
    legacyReceiptId: string | null;
    unheld: boolean;
}): boolean {
    if (args.stagedCount > 0) return true;
    if (args.unheld) return false;
    if (args.uploadedCount > 0) return args.selectedCount > 0;
    return Boolean(args.legacyReceiptId);
}

function formatRubInput(kopecks: number): string {
    return (kopecks / 100).toFixed(2);
}

function parseKopecks(raw: string): number | null {
    const normalized = raw.trim().replace(/\s/g, "").replace(",", ".");
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
    const value = Number(normalized);
    if (!Number.isFinite(value) || value <= 0) return null;
    return Math.round(value * 100);
}

// Подписи источников метки спора. Три пути, четвёртого в системе нет;
// незнакомый код показываем как есть — молчание хуже непонятного слова.
const APPEAL_REASON_LABELS: Record<string, string> = {
    merchant_payment_failed: "мерчант не смог оплатить часть заявки",
    trader: "трейдер",
    claim_timeout: "вышел срок брони заявки площадки",
    external_source: "заявку площадки больше нельзя провести",
    exchange_deposit_review: "спор по переводу USDT",
    exchange_deposit_timeout: "истёк срок поступления USDT",
};

function appealReasonLabel(reason: string): string {
    return APPEAL_REASON_LABELS[reason] ?? reason;
}

function normalizeRate(raw: string): string | null {
    const normalized = raw.trim().replace(/\s/g, "").replace(",", ".");
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
    const value = Number(normalized);
    if (!Number.isFinite(value) || value <= 0 || value > 10000) return null;
    return normalized;
}

export function DisputeDetails({item, initialCaseId = null, onBack, onResolved, onConflict, onReviewChanged}: DisputeDetailsProps): JSX.Element {
    const {token} = useAuth();
    const defaultAmount = useMemo(() => formatRubInput(item.payinParts ? item.payinParts.parts.reduce((sum, p) => sum + (p.confirmedAt ? p.amount : 0), 0) : item.amountRemaining), [item.amountRemaining, item.payinParts]);
    // Курс, зафиксированный при создании заявки: бэк проведёт спор именно по
    // нему, что бы админ ни отправил, поэтому он же и подставляется. Берём его
    // вместо defaultRate — спор по первому платежу фикс-заявки успешных платежей
    // ещё не имеет, и defaultRate там пуст.
    const effectiveRate = resolveEffectiveRate(item.fixedRate, item.defaultRate);
    const fixedRate = effectiveRate.isFixed ? effectiveRate.value : null;
    const defaultRate = effectiveRate.value;
    // Заявка со спором, но БЕЗ активного платежа: она лежит в свободном пуле,
    // а платёж ушёл вместе с трейдером, вернувшим её туда. Метка спора на
    // заявке пережила возврат — ради этой строки UOF-03 и делалась.
    //
    // Разобрать её админ теперь может, и оба исхода настоящие (В-34, А-1):
    // успех открывает платёж на выбранного трейдера и закрывает его, неуспех
    // закрывает заявку без платежа вовсе. У трейдера упала сеть или свет,
    // заявка фактически его, и админ доводит её за него.
    const paymentId = item.paymentId;
    const isExchangeSell = !isSellDisputeActionAllowed(item.workflowKind);
    const unheld = paymentId === null;
    const unheldNote =
        "Держателя у заявки сейчас нет. Закрытие в успех создаст платёж на выбранного трейдера — " +
        "деньги пойдут ему и его команде; закрытие в неуспех отменит заявку без платежа.";
    // Заявка кабинета: у неё два исхода из трёх разговаривают с чужой
    // площадкой, и админ должен видеть, какой именно.
    const isExternal = item.externalSource !== null && item.externalSource !== "";
    const externalSourceLabel = (item.externalSource ?? "").toUpperCase();
    // Статус карточки у площадки, три состояния.
    //
    // `externalFinal` — заявки у площадки больше нет ни в каком рабочем смысле:
    // наружу не уйдёт ничего, что бы админ ни выбрал, и решение остаётся чисто
    // денежным — заплатил наш трейдер или нет. Кабинет наш, и трейдер вправе
    // закрыть сделку прямо в нём: 11.08.2026 так и вышло на двух заявках, а
    // админ видел только спор и выбирал вслепую. Тот же список гасит постановку
    // исходящей команды на бэкенде (`nothingToSendAtEnqueue`), и расходиться им
    // нельзя: обещание «не уйдёт ничего» обязано быть правдой.
    //
    // `externalSettling` — площадка сама доводит заявку. Эти два статуса
    // обратимы (11.08.2026 заявка 697067 ушла из `pre-cancelled` в `completed`),
    // поэтому решение админа наружу всё-таки уходит и обещать обратное нельзя.
    // В пул при этом всё равно нельзя: бэкенд отвечает 409 на оба состояния.
    //
    // Незнакомый статус считается рабочим: тот же выбор делает бэкенд.
    const externalStatus = (item.externalStatus ?? "").toLowerCase();
    const externalFinal = isExternal && ["completed", "cancelled", "unclaimed"].includes(externalStatus);
    const externalSettling = isExternal && ["pre-completed", "pre-cancelled"].includes(externalStatus);
    const externalPoolForbidden = externalFinal || externalSettling;
    const externalTalks = isExternal && !externalFinal;
    const externalClosedNote = `На площадку ${externalSourceLabel} не уйдёт ничего: заявка там уже закрыта${
        externalStatus === "" ? "" : ` (${externalStatus})`
    }.`;
    // Заявка-обмен, по которой клиент уже что-то сделал. Оба «отрицательных»
    // исхода по ней бэкенд разрешает — гейты сигналов стоят только на
    // трейдерских путях, а разбор спора остаётся человеку, — поэтому
    // единственная защита здесь та же, что у заявки кабинета: сказать словами,
    // что произойдёт, до нажатия.
    const exchangeSignals = exchangeClientSignalsSummary(item);
    const exchangeReturnWarning = exchangeOutcomeWarning(item, "return_to_pool");
    const exchangeFailWarning = exchangeOutcomeWarning(item, "fail");

    const [amountInput, setAmountInput] = useState(defaultAmount);
    const [rateInput, setRateInput] = useState(defaultRate);
    const [amountLocked, setAmountLocked] = useState(true);
    const [rateLocked, setRateLocked] = useState(defaultRate !== "");
    const [amountManual, setAmountManual] = useState(false);
    const [rateManual, setRateManual] = useState(defaultRate === "");
    const [successOpen, setSuccessOpen] = useState(false);
    const [receipts, setReceipts] = useState<ReceiptDraft[]>([]);
    const [selectedExistingReceiptIds, setSelectedExistingReceiptIds] = useState<string[]>(
        () => defaultSelectedDisputeReceiptIds(item.uploadedReceipts),
    );
    const [receiptError, setReceiptError] = useState("");
    const [downloadError, setDownloadError] = useState<unknown>(null);
    const [validationError, setValidationError] = useState<unknown>(null);
    const [actionError, setActionError] = useState<unknown>(null);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [resolving, setResolving] = useState(false);
    const [traders, setTraders] = useState<TraderOption[]>([]);
    const [traderId, setTraderId] = useState("");
    const [tradersError, setTradersError] = useState<unknown>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Список трейдеров грузится только у заявки без держателя: только там его и
    // показывают. У заявки с живым платежом трейдер уже есть, и лишний запрос
    // предлагал бы выбор, которого нет.
    useEffect(() => {
        if (!unheld) {
            setTraders([]);
            return;
        }
        let cancelled = false;
        setTradersError(null);
        listTraders(token, {limit: 200})
            .then((resp) => {
                if (cancelled) return;
                setTraders(activeTraderOptions(resp.items));
            })
            .catch((err) => {
                if (!cancelled) setTradersError(err);
            });
        return () => {
            cancelled = true;
        };
    }, [token, unheld, item.orderId]);

    // Reset selected receipts only when the dispute identity/defaults change,
    // using its latest list without resetting operator choices on every poll.
    const uploadedReceiptsForReset = useRef(item.uploadedReceipts);
    uploadedReceiptsForReset.current = item.uploadedReceipts;
    const fullResetAmount = isExchangeSell ? null : defaultAmount;
    useEffect(() => {
        setTraderId("");
        if (fullResetAmount !== null) setAmountInput(fullResetAmount);
        setRateInput(defaultRate);
        setAmountLocked(true);
        setRateLocked(defaultRate !== "");
        setAmountManual(false);
        setRateManual(defaultRate === "");
        setSuccessOpen(false);
        setReceipts([]);
        setSelectedExistingReceiptIds(defaultSelectedDisputeReceiptIds(uploadedReceiptsForReset.current));
        setReceiptError("");
        setDownloadError(null);
        setValidationError(null);
        setActionError(null);
        setPendingAction(null);
        // По заявке, а не по платежу: платёж спорной заявки меняется под
        // админом (вернули в пул, взял другой трейдер), а разбирает он заявку.
    }, [item.orderId, item.paymentId, defaultRate, fullResetAmount]);

    // A sell amount is read-only here, but an adjustment can update it while
    // this details view remains mounted. Synchronize only that field so the
    // open form and staged receipt drafts survive the parent refresh.
    useEffect(() => {
        if (!isExchangeSell) return;
        setAmountInput(defaultAmount);
        setAmountLocked(true);
        setAmountManual(false);
        setPendingAction(null);
    }, [defaultAmount, isExchangeSell, item.orderId]);

    // Ссылки на превью освобождаются поштучно при удалении файла и все разом
    // при уходе со страницы. Через ref, а не через зависимость эффекта: список
    // меняется на каждое добавление, и эффект по нему отзывал бы ссылки у
    // файлов, которые всё ещё показаны.
    const receiptsRef = useRef<ReceiptDraft[]>([]);
    useEffect(() => {
        receiptsRef.current = receipts;
    }, [receipts]);
    useEffect(() => {
        return () => {
            receiptsRef.current.forEach((staged) => URL.revokeObjectURL(staged.objectUrl));
        };
    }, []);

    // Все файлы, ЗАГРУЖЕННЫЕ к спорному платежу. Предъявленного набора у него
    // не существует: набор появляется тем действием, которым платёж закрывают,
    // а спорный платёж не закрыт. Поэтому здесь и заменённый ошибочный файл, и
    // чек неудавшейся попытки — админ разбирает по тому, что есть.
    const uploadedReceipts = item.uploadedReceipts ?? [];

    const downloadUploadedReceipt = async (receiptItem: DisputeReceipt): Promise<void> => {
        if (!receiptItem.receiptUrl) return;
        setDownloadError(null);
        try {
            await downloadReceipt(receiptItem.receiptUrl, token, `${receiptItem.receiptId}.pdf`);
        } catch (err) {
            setDownloadError(err);
        }
    };

    const unlockAmount = (): void => {
        setAmountLocked(false);
        setAmountManual(true);
    };

    const unlockRate = (): void => {
        // Курс фикс-заявки менять некуда: сервер всё равно возьмёт свой.
        if (fixedRate != null) return;
        setRateLocked(false);
        setRateManual(true);
    };

    const resetAmount = (): void => {
        setAmountInput(defaultAmount);
        setAmountLocked(true);
        setAmountManual(false);
    };

    const resetRate = (): void => {
        setRateInput(defaultRate);
        setRateLocked(defaultRate !== "");
        setRateManual(defaultRate === "");
    };

    /**
     * Добавляет выбранные файлы к уже отложенным, а не заменяет их: клиент мог
     * получить перевод несколькими суммами, и чеков тогда столько же. Плохие
     * файлы отсеиваются поимённо — из-за одного неподходящего не пропадают
     * остальные, выбранные тем же движением.
     */
    const handleReceiptFiles = (files: FileList | File[] | null | undefined): void => {
        setReceiptError("");
        const picked = files ? Array.from(files) : [];
        if (picked.length === 0) return;
        const accepted: ReceiptDraft[] = [];
        const rejected: string[] = [];
        for (const file of picked) {
            if (!isSupportedReceiptFile(file)) {
                rejected.push(`${file.name} — неподходящий тип`);
                continue;
            }
            if (file.size > MAX_RECEIPT_BYTES) {
                rejected.push(`${file.name} — больше 15 МБ`);
                continue;
            }
            accepted.push({
                key: `receipt-${receiptKeySeq++}`,
                file,
                fileName: file.name,
                fileSize: file.size,
                objectUrl: URL.createObjectURL(file),
            });
        }
        const errors = [...rejected];
        setReceipts((prev) => {
            const room = MAX_RECEIPTS - selectedExistingReceiptIds.length - prev.length;
            if (accepted.length > room) {
                // Отрезанные файлы называются вслух: молча взять первые
                // несколько читалось бы как «взяли все».
                accepted.slice(room).forEach((extra) => URL.revokeObjectURL(extra.objectUrl));
                errors.push(`Больше ${MAX_RECEIPTS} чеков за раз приложить нельзя — лишние файлы не добавлены.`);
            }
            return [...prev, ...accepted.slice(0, Math.max(room, 0))];
        });
        if (errors.length > 0) setReceiptError(errors.join("; "));
        // Один и тот же файл, выбранный дважды подряд, не даёт change без
        // сброса значения инпута.
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const toggleExistingReceipt = (receiptId: string): void => {
        setReceiptError("");
        setSelectedExistingReceiptIds((previous) => previous.includes(receiptId)
            ? previous.filter((id) => id !== receiptId)
            : previous.length + receipts.length >= MAX_RECEIPTS
              ? previous
              : [...previous, receiptId]);
    };

    const removeReceipt = (key: string): void => {
        setReceiptError("");
        setReceipts((prev) => {
            const staged = prev.find((entry) => entry.key === key);
            if (staged) URL.revokeObjectURL(staged.objectUrl);
            return prev.filter((entry) => entry.key !== key);
        });
    };

    // Чек есть либо среди только что отложенных, либо уже приложен к платежу
    // (редкий случай: чек прошлой попытки).
    const hasReceipt = hasSelectedDisputeReceipt({
        stagedCount: receipts.length,
        uploadedCount: uploadedReceipts.length,
        selectedCount: selectedExistingReceiptIds.length,
        legacyReceiptId: item.receiptId,
        unheld,
    });

    const queueSuccess = (): void => {
        setValidationError(null);
        setActionError(null);
        const traderError = disputeTraderChoiceError(unheld, traderId);
        if (traderError) {
            setValidationError(traderError);
            return;
        }
        const merchantPortalProofReady = item.payinParts ? item.payinParts.parts.every((p) => Boolean(p.confirmedAt || p.failureId)) && item.payinParts.parts.some((p) => p.confirmedAt) : item.merchantPortalIn === true && item.merchantPortalReceiptReady === true;
        if (item.merchantPortalIn === true ? !merchantPortalProofReady : !hasReceipt) {
            setValidationError(item.merchantPortalIn ? "Мерчант должен приложить чек по актуальным реквизитам." : "Для закрытия в успех выберите загруженные чеки или приложите новый файл.");
            return;
        }
        const fiatAmountReal = parseKopecks(amountInput);
        if (fiatAmountReal == null) {
            setValidationError("Сумма факта должна быть положительным числом в рублях, максимум 2 знака после запятой.");
            return;
        }
        if (fiatAmountReal > item.amountRemaining) {
            setValidationError("Сумма факта превышает остаток заявки.");
            return;
        }
        // Порядок проверок здесь важнее самих проверок: сумма больше остатка
        // отвергается выше своим текстом, и до правила «весь остаток» такая
        // сумма не доходит — иначе админ читал бы про недоплату, введя
        // переплату.
        const amountError = disputeSuccessAmountError(item, fiatAmountReal, externalFinal);
        if (amountError) {
            setValidationError(amountError);
            return;
        }
        // Курс фикс-заявки не наш: бэк подставит собственный, а поле здесь
        // только показывает его. Валидировать нечего.
        const currencyRate = fixedRate ?? normalizeRate(rateInput);
        if (currencyRate == null) {
            setValidationError("Курс должен быть положительным числом, максимум 2 знака после запятой.");
            return;
        }
        // У заявки без держателя платёж будет ОТКРЫТ этим действием, и админ
        // обязан прочитать это до нажатия: он не «закрывает спор», он заводит
        // платёж на чужое имя и начисляет по нему деньги.
        const chosenTraderLabel = traders.find((option) => option.id === traderId)?.label ?? traderId;
        const unheldPrefix = unheld
            ? `Платёж будет создан на трейдера «${chosenTraderLabel}» — начисление пойдёт ему и его команде. `
            : "";
        const lowerActualHeldPayout = item.workflowKind === "standard_payout" &&
            !isExternal && !unheld && fiatAmountReal < item.amountRemaining;
        const lowerActualConfirmation = lowerActualHeldPayout
            ? `Факт оплаты: ${formatAmount(fiatAmountReal)} ₽. Неоплаченный остаток: ${formatAmount(item.amountRemaining - fiatAmountReal)} ₽. Заявка будет закрыта, остаток не вернётся в пул, новый платёж не создаётся.`
            : null;
        setPendingAction({
            outcome: "success",
            merchantStatus: "OK",
            title: unheld ? "Закрыть заявку за трейдера" : "Закрыть спор в успех",
            message:
                unheldPrefix +
                (externalTalks
                    ? `Платёж будет закрыт как успешный и проведён по книге. Мерчанту уйдёт статус OK, на площадку ${externalSourceLabel} — подтверждение оплаты.`
                    : externalFinal
                      ? `Платёж будет закрыт как успешный и проведён по книге. Мерчанту уйдёт статус OK. ${externalClosedNote}`
                      : lowerActualConfirmation ?? "Платёж будет закрыт как успешный. Мерчанту уйдёт статус OK."),
            confirmLabel: "Закрыть в успех",
            buttonClass: "btn-success",
            body: {outcome: "success", fiatAmountReal, currencyRate},
            receipts,
            existingReceiptIds: unheld ? [] : selectedExistingReceiptIds,
            traderId: unheld ? traderId : undefined,
        });
    };

    const queueReturnToPool = (): void => {
        if (isExchangeSell) {
            setValidationError("Продажу USDT нельзя возвращать в пул из общего спора.");
            return;
        }
        setValidationError(null);
        setActionError(null);
        if (unheld) {
            setValidationError("Заявка и так в пуле — возвращать её туда нечем.");
            return;
        }
        setPendingAction({
            outcome: "return_to_pool",
            merchantStatus: "PROCESS",
            title: "Вернуть заявку в пул",
            // Ветки «площадка закрыла заявку сама» здесь нет намеренно: в этом
            // состоянии кнопка выключена, а бэкенд отвечает 409. Возврат в пул
            // выдал бы трейдеру работу, которой у площадки уже нет.
            message: exchangeReturnWarning
                ? `${exchangeReturnWarning} Текущий спорный платёж будет отменён, мерчанту уйдёт статус PROCESS.`
                : isExternal
                  ? "Текущий спорный платёж будет отменён, заявка снова станет доступна трейдерам. Мерчанту уйдёт статус PROCESS. На площадку не уйдёт ничего — это наше внутреннее движение."
                  : "Текущий спорный платёж будет отменён, заявка снова станет доступна трейдерам. Мерчанту уйдёт статус PROCESS.",
            confirmLabel: exchangeReturnWarning ? "Всё равно вернуть в пул" : "Вернуть в пул",
            buttonClass: "btn-appeal",
            body: {outcome: "return_to_pool"},
            receipts: [],
            existingReceiptIds: [],
        });
    };

    const queueFail = (): void => {
        if (isExchangeSell) {
            setValidationError("Продажу USDT нельзя отменять из общего спора.");
            return;
        }
        setValidationError(null);
        setActionError(null);
        const failureError = disputeFailureError(item);
        if (failureError) {
            setValidationError(failureError);
            return;
        }
        setPendingAction({
            outcome: "fail",
            merchantStatus: "CANCEL",
            title: externalTalks ? "Отменить заявку и сообщить площадке" : "Закрыть спор в неуспех",
            // Единственное место во всей админке, откуда что-то уходит на чужую
            // площадку по решению человека — и только пока карточка у них живая.
            // На закрытой отмена никуда не уйдёт, и обещать обратное нельзя:
            // «терминально отменённой» админ прочитает как про нашу базу.
            message: exchangeFailWarning
                ? `${exchangeFailWarning} Мерчанту уйдёт статус CANCEL.`
                : externalTalks
                  ? `Заявка будет отменена окончательно, и отмена уйдёт на площадку ${externalSourceLabel}. Отменить это решение нельзя — ни у нас, ни там. Мерчанту уйдёт статус CANCEL.`
                  : externalFinal
                    ? `Заявка будет отменена окончательно у нас, без начисления трейдеру. ${externalClosedNote} Мерчанту уйдёт статус CANCEL.`
                    : "Заявка станет терминально отменённой. Мерчанту уйдёт статус CANCEL.",
            confirmLabel: exchangeFailWarning
                ? "Всё равно отменить"
                : externalTalks
                  ? "Отменить и отправить на площадку"
                  : "Закрыть в неуспех",
            buttonClass: "btn-danger",
            body: {outcome: "fail"},
            receipts: [],
            existingReceiptIds: [],
        });
    };

    const confirmAction = async (): Promise<void> => {
        if (!pendingAction || !token) return;
        setResolving(true);
        setActionError(null);
        try {
            // Неуспех по заявке без держателя идёт заявочной ручкой: платежа,
            // которым можно было бы ключевать запрос, у неё нет — ради этого
            // случая дверь и заводилась.
            if (unheld && pendingAction.outcome === "fail") {
                await resolveOrderDispute(token, item.orderId);
                const status = pendingAction.merchantStatus;
                setPendingAction(null);
                onResolved(item.orderId, status);
                return;
            }

            // Успех по заявке без держателя: платёж → чек → закрытие. Порядок
            // задан связями, а не вкусом: закрытие требует чек, чек привязан к
            // платежу, и до создания платежа прикладывать его не к чему.
            let targetPaymentID = paymentId;
            if (targetPaymentID === null) {
                if (!pendingAction.traderId) return;
                const opened = await openDisputePayment(token, item.orderId, pendingAction.traderId);
                targetPaymentID = opened.id;
            }

            // Чеки загружаются до разбора — как у трейдера, где загрузка тоже
            // предшествует закрытию. Загрузка возвращает id, и именно этот
            // набор уходит в разбор: без него платёж закрылся бы последним
            // загруженным файлом, а остальные остались бы лежать у нас.
            // Последовательно, а не пачкой: ручка принимает один файл, и при
            // отказе на середине понятно, сколько уже доехало.
            const receiptIds: string[] = pendingAction.existingReceiptIds.slice();
            if (pendingAction.outcome === "success") {
                for (const staged of pendingAction.receipts) {
                    if (staged.receiptId) {
                        receiptIds.push(staged.receiptId);
                        continue;
                    }
                    const uploaded = await uploadDisputeReceipt(token, targetPaymentID, staged.file);
                    receiptIds.push(uploaded.receiptId);
                    staged.receiptId = uploaded.receiptId;
                    setReceipts((previous) => previous.map((entry) =>
                        entry.key === staged.key ? {...entry, receiptId: uploaded.receiptId} : entry,
                    ));
                    setPendingAction((previous) => previous
                        ? {
                            ...previous,
                            receipts: previous.receipts.map((entry) =>
                                entry.key === staged.key ? {...entry, receiptId: uploaded.receiptId} : entry,
                            ),
                        }
                        : previous);
                }
            }
            const uniqueReceiptIds = Array.from(new Set(receiptIds));
            await resolveDispute(token, targetPaymentID,
                uniqueReceiptIds.length > 0 ? {...pendingAction.body, receiptIds: uniqueReceiptIds} : pendingAction.body);
            const status = pendingAction.merchantStatus;
            setPendingAction(null);
            onResolved(item.orderId, status);
        } catch (err) {
            setActionError(err);
            if (err instanceof ApiError && err.status === 409) {
                onConflict(err);
            }
        } finally {
            setResolving(false);
        }
    };

    const failError = disputeFailureError(item);
    const failBlocked = failError !== null;
    const pendingReceiptCount = pendingAction
        ? pendingAction.existingReceiptIds.length + pendingAction.receipts.length
        : 0;

    return (
        <div className="page dispute-details">
            <div className="page-header">
                <div className="merchant-details-title">
                    <button className="btn btn-ghost btn-sm" type="button" onClick={onBack}>
                        ← К списку
                    </button>
                    <h2>Dispute {item.remoteId}</h2>
                </div>
            </div>

            <ErrorBanner error={validationError} title="Проверьте действие" />
            <ErrorBanner error={actionError} title="Не удалось разрешить спор" />
            <ErrorBanner error={downloadError} title="Не удалось скачать чек" />
            {!item.payinParts && <FailureEvidenceList orderId={item.orderId} operator/>}
            {item.payinParts ? <PayinParts orderId={item.orderId} state={item.payinParts} audience="operator" onConfirm={async (number, receipt, revision) => {
                await confirmPayinPart(token, item.payinParts!.paymentId, number, item.payinParts!.requisitesVersion, receipt, true, revision);
                onReviewChanged?.();
            }} onChanged={onReviewChanged}/> : <PortalReceiptList orderId={item.orderId} audience="operator" title="Чеки мерчанта для проверки" />}

            {item.paymentId && <ExternalCompletionDetails paymentId={item.paymentId} completion={item.externalCompletion} token={token} canConfirm/>}
            <section className="merchant-details-card dispute-info-card">
                <div className="trader-meta-grid">
                    <Meta label="Order ID" value={item.orderId} mono />
                    <Meta label="Payment ID" value={paymentId ?? "нет активного платежа"} mono={!unheld} />
                    <Meta label="remoteId" value={item.remoteId} mono />
                    <Meta label="Мерчант" value={item.merchantName} />
                    <Meta label="Трейдер" value={item.traderName ?? "в пуле, держателя нет"} />
                    <Meta label="Сумма" value={`${formatAmount(item.amount)} ₽`} mono />
                    <Meta label="Остаток" value={`${formatAmount(item.amountRemaining)} ₽`} mono />
                    {/* Только проведённые платежи: лимит дробления на разбор
                        спора не влияет (закрытие идёт на весь остаток при любом
                        `maxPayments`), а «0 / 0» у заявки кабинета читалось как
                        правило, которого нет. Проведённые оставлены — ими
                        объясняется погашенная кнопка «в неуспех». */}
                    <Meta label="Проведено платежей" value={String(item.paymentsMade)} mono />
                    <Meta label="Валюта" value={`${item.currency} / scale ${item.currencyScale}`} mono />
                    <Meta label="Открыт спор" value={formatDate(item.appealedAt)} />
                    {/* Источник метки. У строки без платежа он единственное, что
                        отвечает на вопрос «почему заявка здесь»: трейдера в ней
                        нет, и спросить не у кого. */}
                    <Meta label="Спор поднял" value={appealReasonLabel(item.appealReason)} />
                    {item.appealMessage && <Meta label="Причина обращения" value={item.appealMessage} />}
                    <Meta label="Последний чек" value={item.receiptId ?? "нет"} mono={item.receiptId !== null} />
                    <Meta label="Курс по умолчанию" value={item.defaultRate ?? "нет"} mono />
                </div>

                {/* Все файлы платежа, а не последний. Трейдер, заплативший тремя
                    переводами, приложил три, и разбирать спор по одному из них
                    значит разбирать вслепую. Подпись честная: это ЗАГРУЖЕННЫЕ
                    файлы — предъявленного набора у незакрытого платежа не
                    существует, он появляется при закрытии. */}
                <div className="dispute-receipts">
                    <span className="meta-label">Загруженные к платежу файлы</span>
                    {uploadedReceipts.length === 0 ? (
                        <span className="muted">чеков нет</span>
                    ) : (
                        <ul className="dispute-receipts-list">
                            {uploadedReceipts.map((uploaded, index) => (
                                <li key={uploaded.receiptId}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selectedExistingReceiptIds.includes(uploaded.receiptId)}
                                            onChange={() => toggleExistingReceipt(uploaded.receiptId)}
                                            disabled={resolving || unheld}
                                        />
                                        предъявить
                                    </label>
                                    <span className="mono">{index + 1}. {uploaded.receiptId}</span>
                                    <span className="muted">{formatDate(uploaded.uploadedAt)}</span>
                                    {uploaded.receiptUrl ? (
                                        <a
                                            href={uploaded.receiptUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                void downloadUploadedReceipt(uploaded);
                                            }}
                                        >
                                            скачать
                                        </a>
                                    ) : (
                                        <span className="muted">ссылки нет</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <section className="dispute-resolution-panel">
                <div className="section-header">
                    <div>
                        <h3>Разрешение спора</h3>
                        <span>OK / PROCESS / CANCEL</span>
                        {/* Сигналы клиента заявки-обмена стоят там же, где
                            состояние заявки кабинета, и по той же причине: это
                            вводные для решения, а не подробность карточки. */}
                        {exchangeSignals && (
                            <span className="dispute-external-state">
                                Заявка-обмен. {exchangeSignals} Оба «отрицательных» исхода оставят
                                деньги клиента без адресата.
                            </span>
                        )}
                        {isExternal && (
                            <span className="dispute-external-state">
                                {externalFinal
                                    ? `Заявка кабинета ${externalSourceLabel}: площадка уже закрыла её сама${
                                          externalStatus === "" ? "" : ` (${externalStatus})`
                                      } — наружу не уйдёт ничего, решаете только про наши деньги.`
                                    : externalSettling
                                      ? `Заявка кабинета ${externalSourceLabel}: площадка сама доводит её${
                                            externalStatus === "" ? "" : ` (${externalStatus})`
                                        } — ваше решение к ней уйдёт, но принять его она уже может отказаться.`
                                      : `Заявка кабинета ${externalSourceLabel}, статус у площадки: ${
                                            externalStatus === "" ? "неизвестен" : externalStatus
                                        }.`}
                            </span>
                        )}
                    </div>
                </div>

                {unheld && <p className="resolution-hint">{unheldNote}</p>}

                <div className="resolution-grid">
                    <div className={resolutionCardClass("success", "success-card")}>
                        <div>
                            <strong>{unheld ? "Закрыть за трейдера" : "Закрыть в успех"}</strong>
                            <span>
                                {unheld ? "Мерчанту уйдёт OK, начисление — выбранному трейдеру" : "Мерчанту уйдёт OK"}
                            </span>
                        </div>
                        <button
                            className="btn btn-success"
                            type="button"
                            onClick={() => setSuccessOpen((prev) => !prev)}
                            disabled={resolving}
                        >
                            {successOpen ? "Скрыть форму" : "Открыть форму"}
                        </button>
                    </div>

                    {/* В-32: карточка «вернуть в пул» СКРЫТА, а не удалена —
                        `display: none` живёт на классе. Ручка, исход
                        `return_to_pool` и вся его логика остаются нетронутыми:
                        возможно, вернётся в более сложном виде, и тогда это одна
                        строка CSS, а не восстановление удалённого кода. */}
                    <div className={resolutionCardClass("return_to_pool")}>
                        <div>
                            <strong>Вернуть в пул</strong>
                            <span>
                                {unheld
                                    ? "Заявка уже в пуле"
                                    : externalPoolForbidden
                                      ? "Нельзя: заявка больше не в работе у площадки"
                                      : "Мерчанту уйдёт PROCESS"}
                            </span>
                        </div>
                        <button
                            className="btn btn-appeal"
                            type="button"
                            onClick={queueReturnToPool}
                            disabled={isExchangeSell || unheld || externalPoolForbidden || resolving}
                            title={
                                externalPoolForbidden
                                    ? "площадка закрывает заявку сама — закройте спор деньгами, а не возвратом в пул"
                                    : undefined
                            }
                        >
                            Вернуть в пул
                        </button>
                    </div>

                    <div className={resolutionCardClass("fail", "danger-card")}>
                        <div>
                            <strong>Закрыть в неуспех</strong>
                            <span>
                                {externalTalks
                                    ? `Мерчанту уйдёт CANCEL, отмена — на ${externalSourceLabel}`
                                    : externalFinal
                                      ? "Мерчанту уйдёт CANCEL, на площадку — ничего"
                                      : "Мерчанту уйдёт CANCEL"}
                            </span>
                        </div>
                        {isExchangeSell ? (
                            <CloseSellDispute key={item.orderId} orderId={item.orderId} onClosed={() => onResolved(item.orderId, "CANCEL")} />
                        ) : <button
                            className="btn btn-danger"
                            type="button"
                            onClick={queueFail}
                            disabled={isExchangeSell || failBlocked || resolving}
                            title={failError ?? undefined}
                        >
                            Закрыть в неуспех
                        </button>}
                        {!isExchangeSell && failError && <p className="resolution-hint danger-text">{failError}</p>}
                    </div>
                </div>

                {successOpen && (
                    <div className="success-resolution-form">
                        {/* Выпадающий список трейдеров — обычный, без поиска,
                            иерархии и группировки по командам: трейдеров
                            единицы (В-34). Показывается только у заявки без
                            держателя: у заявки с живым платежом трейдер уже
                            есть, и закрытие переназначать его не имеет права. */}
                        {unheld && (
                            <TraderChoiceField
                                traders={traders}
                                value={traderId}
                                error={tradersError}
                                onChange={setTraderId}
                                disabled={resolving}
                            />
                        )}
                        {uploadedReceipts.length > 1 && selectedExistingReceiptIds.length === 0 && (
                            <p className="resolution-hint">
                                Выберите чеки, которыми закрываете платёж. При нескольких загруженных чеках выбор обязателен.
                            </p>
                        )}
                        <ReceiptField
                            inputRef={fileInputRef}
                            receipts={receipts}
                            error={receiptError}
                            hasExistingReceipt={Boolean(item.receiptId)}
                            onFiles={handleReceiptFiles}
                            onRemove={removeReceipt}
                        />
                        <LockedField
                            id="dispute-amount-real"
                            label="Сумма факта, ₽"
                            value={amountInput}
                            locked={amountLocked}
                            manual={amountManual}
                            onChange={setAmountInput}
                            onUnlock={unlockAmount}
                            onReset={resetAmount}
                        />
                        <LockedField
                            id="dispute-currency-rate"
                            label="Курс"
                            value={rateInput}
                            locked={rateLocked || fixedRate != null}
                            manual={rateManual}
                            onChange={setRateInput}
                            onUnlock={unlockRate}
                            onReset={resetRate}
                            placeholder="например 80.25"
                            fixedHint={
                                fixedRate != null
                                    ? "Курс зафиксирован при создании заявки — спор закроется по нему."
                                    : undefined
                            }
                        />
                        <div className="success-resolution-actions">
                            <button className="btn btn-success" type="button" onClick={queueSuccess} disabled={resolving}>
                                Закрыть в успех
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {isExchangeSell && (
                <AdminDepositCasesPage key={item.orderId} orderId={item.orderId} initialCaseId={initialCaseId} onReviewChanged={onReviewChanged}/>
            )}

            <Modal
                open={pendingAction !== null}
                title={pendingAction?.title ?? "Подтверждение"}
                onClose={() => {
                    if (!resolving) setPendingAction(null);
                }}
                sizeClassName="modal-sm"
                disableClose={resolving}
            >
                {pendingAction && (
                    <>
                        <p className="modal-hint">{pendingAction.message}</p>
                        <div className="confirm-summary dispute-confirm-summary">
                            <InfoRow label="remoteId" value={item.remoteId} mono />
                            <InfoRow
                                label="paymentId"
                                value={paymentId ?? "будет создан"}
                                mono={paymentId !== null}
                            />
                            {/* Владелец денег называется в окне подтверждения, а
                                не только в форме: это последний экран перед
                                начислением на чужую команду. */}
                            {pendingAction.traderId && (
                                <InfoRow
                                    label="Деньги трейдеру"
                                    value={
                                        traders.find((option) => option.id === pendingAction.traderId)?.label ??
                                        pendingAction.traderId
                                    }
                                />
                            )}
                            <InfoRow label="Статус мерчанту" value={pendingAction.merchantStatus} mono />
                            {pendingAction.outcome === "success" && (
                                <>
                                    <InfoRow label="Сумма факта" value={`${formatAmount(pendingAction.body.fiatAmountReal)} ₽`} mono />
                                    <InfoRow label="Курс" value={pendingAction.body.currencyRate ?? ""} mono />
                                    <InfoRow
                                        label={pendingReceiptCount > 1 ? "Чеки" : "Чек"}
                                        value={
                                            pendingReceiptCount === 0
                                                ? "1 — ранее приложенный чек"
                                                : `${pendingReceiptCount}: ${[
                                                    ...pendingAction.existingReceiptIds,
                                                    ...pendingAction.receipts.map((staged) => staged.fileName),
                                                ].join(", ")}`
                                        }
                                        mono={pendingReceiptCount === 0}
                                    />
                                </>
                            )}
                        </div>
                        <ErrorBanner error={actionError} />
                        <div className="modal-actions">
                            <button
                                className="btn btn-ghost"
                                type="button"
                                onClick={() => setPendingAction(null)}
                                disabled={resolving}
                            >
                                Отмена
                            </button>
                            <button
                                className={`btn ${pendingAction.buttonClass}`}
                                type="button"
                                onClick={confirmAction}
                                disabled={resolving}
                            >
                                {resolving ? "Выполнение..." : pendingAction.confirmLabel}
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}

function Meta({label, value, mono = false}: {label: string; value: string; mono?: boolean}): JSX.Element {
    return (
        <div className="meta-card">
            <span className="meta-label">{label}</span>
            <span className={`meta-value ${mono ? "mono" : ""}`.trim()}>{value}</span>
        </div>
    );
}

function InfoRow({label, value, mono = false}: {label: string; value: string; mono?: boolean}): JSX.Element {
    return (
        <div className="info-row">
            <span>{label}</span>
            <strong className={mono ? "mono" : undefined}>{value}</strong>
        </div>
    );
}

/**
 * Выбор трейдера, за которым числятся деньги (В-34).
 *
 * Пустое значение по умолчанию — не забывчивость: подставленный «первый из
 * списка» означал бы, что человек может начислить деньги чужой команде, ни разу
 * не посмотрев в это поле.
 */
function TraderChoiceField({
    traders,
    value,
    error,
    onChange,
    disabled,
}: {
    traders: TraderOption[];
    value: string;
    error: unknown;
    onChange: (next: string) => void;
    disabled: boolean;
}): JSX.Element {
    return (
        <div className="locked-field dispute-trader-field">
            <div className="locked-field-label">
                <label htmlFor="dispute-trader">Трейдер</label>
                <span>деньги пойдут ему и его команде</span>
            </div>
            <select
                id="dispute-trader"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled || traders.length === 0}
            >
                <option value="">— выберите трейдера —</option>
                {traders.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.label}
                    </option>
                ))}
            </select>
            {traders.length === 0 && !error && (
                <small className="field-hint muted">Активных трейдеров нет — закрыть за кого-то не получится.</small>
            )}
            <ErrorBanner error={error} title="Не удалось загрузить список трейдеров" />
        </div>
    );
}

function humanSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(2)} МБ`;
}

/**
 * Поле чеков разбора. Файлов может быть несколько: клиент получил перевод не
 * одной суммой, и на площадку обязаны уехать все чеки, а не последний
 * загруженный (задача от 18.08.2026).
 *
 * Дозагрузка, а не замена: выбранное добавляется к уже отложенному, поэтому
 * файлы можно набирать по одному, если выбрать их разом не получилось.
 */
function ReceiptField({
    inputRef,
    receipts,
    error,
    hasExistingReceipt,
    onFiles,
    onRemove,
}: {
    inputRef: React.RefObject<HTMLInputElement>;
    receipts: ReceiptDraft[];
    error: string;
    hasExistingReceipt: boolean;
    onFiles: (files: FileList | File[] | null | undefined) => void;
    onRemove: (key: string) => void;
}): JSX.Element {
    const [dragging, setDragging] = useState(false);
    const full = receipts.length >= MAX_RECEIPTS;

    return (
        <div className="dispute-receipt-field">
            <div className="locked-field-label">
                <label>Чеки</label>
                <span>{receipts.length > 0 ? `выбрано: ${receipts.length}` : "обязателен для успеха"}</span>
            </div>
            <input
                ref={inputRef}
                type="file"
                accept={RECEIPT_FILE_ACCEPT}
                multiple
                hidden
                onChange={(e) => onFiles(e.target.files)}
            />
            {receipts.map((staged) => (
                <div className="receipt-ready-card" key={staged.key}>
                    <div className="receipt-file-icon">ЧЕК</div>
                    <div className="receipt-file-main">
                        <strong>{staged.fileName}</strong>
                        <span>{humanSize(staged.fileSize)} · готов к отправке</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => window.open(staged.objectUrl, "_blank", "noopener,noreferrer")}>
                        Просмотреть
                    </button>
                    <button className="btn btn-ghost btn-sm danger-text" type="button" onClick={() => onRemove(staged.key)}>
                        Удалить
                    </button>
                </div>
            ))}
            {!full && (
                <div
                    className={`receipt-dropzone ${dragging ? "dragging" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => inputRef.current?.click()}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            inputRef.current?.click();
                        }
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragging(false);
                        onFiles(e.dataTransfer.files);
                    }}
                >
                    <div className="receipt-drop-icon">ЧЕК</div>
                    <strong>
                        {receipts.length > 0
                            ? "Добавить ещё чек"
                            : hasExistingReceipt
                              ? "Заменить чек"
                              : "Перетащите чеки или выберите файлы"}
                    </strong>
                    <span>{RECEIPT_FILE_HINT} · можно несколько</span>
                </div>
            )}
            {full && <div className="receipt-existing-note">Приложено {MAX_RECEIPTS} чеков — это максимум на одно закрытие.</div>}
            {hasExistingReceipt && receipts.length === 0 && (
                <div className="receipt-existing-note">К заявке уже приложен чек — можно закрыть без повторной загрузки.</div>
            )}
            {error && <div className="receipt-error">{error}</div>}
        </div>
    );
}

function LockedField({
    id,
    label,
    value,
    locked,
    manual,
    onChange,
    onUnlock,
    onReset,
    placeholder,
    fixedHint,
}: {
    id: string;
    label: string;
    value: string;
    locked: boolean;
    manual: boolean;
    onChange: (next: string) => void;
    onUnlock: () => void;
    onReset: () => void;
    placeholder?: string;
    /** Задан = значение зафиксировано системой: править нечего, кнопки нет. */
    fixedHint?: string;
}): JSX.Element {
    const permanent = fixedHint != null;
    return (
        <div className={`locked-field ${manual ? "manual" : ""}`.trim()}>
            <div className="locked-field-label">
                <label htmlFor={id}>{label}</label>
                {permanent ? <span>зафиксирован</span> : manual ? <span>ручной ввод</span> : <span>по умолчанию</span>}
            </div>
            <div className="locked-field-row">
                <input
                    id={id}
                    className="mono"
                    type="text"
                    inputMode="decimal"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={locked}
                    placeholder={placeholder}
                />
                {permanent ? null : locked ? (
                    <button className="btn btn-ghost btn-sm lock-button" type="button" onClick={onUnlock} title="Разблокировать ручной ввод">
                        Изменить
                    </button>
                ) : (
                    <button className="btn btn-ghost btn-sm lock-button" type="button" onClick={onReset} title="Вернуть значение по умолчанию">
                        Сбросить
                    </button>
                )}
            </div>
            {permanent && <small className="field-hint muted">{fixedHint}</small>}
        </div>
    );
}
